import { useState, useEffect, useCallback } from "react";
import { Checkbox, Card, Typography, Space, Skeleton } from "antd";
import permissionsReference from "../permissions_reference";
import "./PermissionsSelector.scss";

const { Title } = Typography;

const PermissionsSelector = ({
    value = [],
    onChange,
    permissions = [],
    loading = false,
    disabled = false
}) => {
    const createPermissionMap = () => {
        const map = new Map();
        permissions.forEach(permission => {
            map.set(permission.codename, permission);
        });
        return map;
    };

    const permissionMap = createPermissionMap();

    // Convert permission IDs to codenames for initial state
    const getCodenamesFromIds = useCallback((ids) => {
        const codenames = new Set();
        ids.forEach(id => {
            const permission = permissions.find(p => p.id.toString() === id);
            if (permission) {
                codenames.add(permission.codename);
            }
        });
        return codenames;
    }, [permissions]);

    const [selectedPermissions, setSelectedPermissions] = useState(() =>
        getCodenamesFromIds(value)
    );

    useEffect(() => {
        setSelectedPermissions(getCodenamesFromIds(value));
    }, [value, getCodenamesFromIds]);

    // Flatten permissions including children for searching
    const findPermissionInReference = (codename) => {
        for (const section of permissionsReference) {
            for (const permission of section.permissions) {
                if (permission.codename === codename) return permission;
                if (permission.children) {
                    const child = permission.children.find(c => c.codename === codename);
                    if (child) return child;
                }
            }
        }
        return null;
    };

    // Get all permissions that depend on a given codename (including children)
    const getPermissionsDependingOn = (codename) => {
        const dependents = [];
        permissionsReference.forEach(section => {
            section.permissions.forEach(permission => {
                if (permission.depends_on?.includes(codename)) {
                    dependents.push(permission.codename);
                }
                if (permission.children) {
                    permission.children.forEach(child => {
                        if (child.depends_on?.includes(codename)) {
                            dependents.push(child.codename);
                        }
                    });
                }
            });
        });
        return dependents;
    };

    const handlePermissionChange = (codename, checked) => {
        const newSelected = new Set(selectedPermissions);

        if (checked) {
            newSelected.add(codename);

            // Handle dependencies - auto-check dependents
            const referencePermission = findPermissionInReference(codename);
            if (referencePermission?.depends_on) {
                referencePermission.depends_on.forEach(dependency => {
                    newSelected.add(dependency);
                });
            }
        } else {
            newSelected.delete(codename);

            // Handle reverse dependencies - uncheck permissions that depend on this one
            const dependents = getPermissionsDependingOn(codename);
            dependents.forEach(dep => {
                newSelected.delete(dep);
            });
        }

        setSelectedPermissions(newSelected);

        // Convert to array of permission IDs for the parent component
        const selectedIds = Array.from(newSelected)
            .map(codename => permissionMap.get(codename)?.id?.toString())
            .filter(Boolean);

        // Call onChange to update the form
        if (onChange) {
            onChange(selectedIds);
        }
    };

    const isPermissionAvailable = (codename) => {
        return permissionMap.has(codename);
    };

    const renderPermission = (permission, isChild = false) => {
        const isAvailable = isPermissionAvailable(permission.codename);
        const isChecked = selectedPermissions.has(permission.codename);

        if (!isAvailable) return null;

        return (
            <Checkbox
                key={permission.codename}
                checked={isChecked}
                disabled={disabled}
                onChange={(e) => handlePermissionChange(permission.codename, e.target.checked)}
                className={isChild ? 'child-permission' : (permission.depends_on ? 'dependent-permission' : '')}
            >
                <span className="permission-name">
                    {permission.name}
                </span>
                {permission.depends_on && !isChild && (
                    <span className="dependency-indicator">
                        (depende de: {permission.depends_on.map(dep => {
                            const depRef = findPermissionInReference(dep);
                            return depRef?.name || dep;
                        }).join(', ')})
                    </span>
                )}
            </Checkbox>
        );
    };

    if (loading) {
        return (
            <div className="permissions-selector">
                <Skeleton active paragraph={{ rows: 6 }} />
            </div>
        );
    }

    return (
        <div className="permissions-selector">
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                {permissionsReference.map((section, sectionIndex) => (
                    <Card
                        key={sectionIndex}
                        size="small"
                        className="permission-section"
                    >
                        <Title level={5} className="section-title">
                            {section.title}
                        </Title>
                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            {section.permissions.map((permission) => {
                                const mainPermission = renderPermission(permission, false);
                                const childPermissions = permission.children?.map(child =>
                                    renderPermission(child, true)
                                ).filter(Boolean);

                                if (!mainPermission && (!childPermissions || childPermissions.length === 0)) {
                                    return null;
                                }

                                return (
                                    <div key={permission.codename} className="permission-group">
                                        {mainPermission}
                                        {childPermissions && childPermissions.length > 0 && (
                                            <div className="children-permissions">
                                                {childPermissions}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </Space>
                    </Card>
                ))}
            </Space>
        </div>
    );
};

export default PermissionsSelector;
